(() => {
  'use strict';

  function init(root) {
    const navLinks = Array.from(root.querySelectorAll('[data-doc]'));
    const docs = Array.from(root.querySelectorAll('[data-doc-body]'));
    if (navLinks.length === 0) return;

    function activate(key, updateHash) {
      const match = navLinks.some((link) => link.dataset.doc === key);
      if (!match) return;
      navLinks.forEach((link) => link.classList.toggle('is-active', link.dataset.doc === key));
      docs.forEach((doc) => doc.classList.toggle('is-active', doc.dataset.docBody === key));
      if (updateHash) history.replaceState(null, '', '#' + key);
      root.scrollIntoView({ behavior: 'instant', block: 'start' });
    }

    navLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        activate(link.dataset.doc, true);
      });
    });

    const initial = window.location.hash.replace('#', '');
    if (initial) activate(initial, false);
  }

  // Long-form legal pages: build the sidebar contents from the h2s in the page body, so the
  // body stays plain portable HTML that can also be pasted into Settings → Policies.
  function initLegalDoc(body) {
    const toc = document.querySelector('[data-legal-toc]');
    if (!toc) return;

    const headings = Array.from(body.querySelectorAll('h2'));
    if (headings.length < 2) return;

    // Phones get a second copy of the contents, collapsed, directly under the document
    // title — and the sidebar one is hidden by CSS at that width.
    //
    // The sidebar cannot simply be reflowed: stacked above the article it put a wrapped
    // row of bare link text between the eyebrow and the H1, which reads as a broken
    // paragraph rather than as navigation, and pushed the title of the document a screen
    // down behind it. A document should open with its own name.
    //
    // Built here rather than in Liquid because the headings themselves are only discovered
    // at runtime — the page body is plain portable HTML with no anchors in it.
    let mobileList = null;
    const head = body.querySelector('.meningea-legaldoc__head');
    if (head) {
      const details = document.createElement('details');
      details.className = 'meningea-legaldoc__mtoc';
      const summary = document.createElement('summary');
      // The document language decides the label; the element is built at runtime, so it
      // cannot come from a Liquid translation key like the rest of the theme's copy.
      const lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
      const contentsLabel = lang === 'de' ? 'Inhalt' : 'Contents';
      summary.innerHTML =
        '<span>' + contentsLabel + '</span>' +
        '<svg width="10" height="7" viewBox="0 0 9 6" fill="none" aria-hidden="true">' +
        '<path d="M1 1l3.5 3.5L8 1" stroke="currentColor" stroke-width="1.3" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      mobileList = document.createElement('nav');
      mobileList.setAttribute('aria-label', lang === 'de' ? 'Auf dieser Seite' : 'On this page');
      details.append(summary, mobileList);
      head.after(details);

      // Collapse again after a jump, so the reader lands on the section instead of on the
      // open index they just used.
      mobileList.addEventListener('click', (e) => {
        if (e.target.closest('a')) details.removeAttribute('open');
      });
    }

    const slug = (text) =>
      text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .slice(0, 60);

    const links = headings.map((heading, i) => {
      if (!heading.id) heading.id = slug(heading.textContent) || 'section-' + i;
      const a = document.createElement('a');
      a.href = '#' + heading.id;
      a.textContent = heading.textContent;
      toc.appendChild(a);
      if (mobileList) {
        const m = a.cloneNode(true);
        mobileList.appendChild(m);
      }
      return a;
    });

    // Highlight whichever heading is currently topmost in the viewport.
    if (!('IntersectionObserver' in window)) return;
    const seen = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => seen.set(entry.target, entry.isIntersecting));
        const activeIndex = headings.findIndex((h) => seen.get(h));
        links.forEach((link, i) => link.classList.toggle('is-active', i === activeIndex));
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    headings.forEach((h) => observer.observe(h));
  }

  // Copies each column's heading onto the cells beneath it, so that on a phone the CSS can
  // stack every row into a labelled block instead of leaving a table wider than the screen.
  //
  // The first attempt at this was CSS-only — the table scrolled sideways inside its own
  // track. That is fine for a wide numeric grid and wrong for these: the shipping table's
  // whole purpose is comparing a destination against a delivery time, and in a 375px window
  // you could only ever see one and a half of its four columns at once. The privacy table
  // is worse still, because its cells hold sentences.
  //
  // Done here rather than in the page markup for the same reason the contents list is: the
  // page body has to stay plain portable HTML that can be pasted into Settings → Policies.
  function initLegalTables(body) {
    body.querySelectorAll('table').forEach((table) => {
      let headRow = table.querySelector('thead tr');
      if (!headRow) {
        const first = table.rows[0];
        if (first && first.querySelector('th')) headRow = first;
      }
      if (!headRow) return;

      const labels = Array.from(headRow.cells).map((c) => c.textContent.trim());
      // A header row sitting loose in the tbody has to be hidden by the stacked layout too;
      // one inside <thead> is already covered by `thead { display: none }`.
      if (!headRow.closest('thead')) headRow.setAttribute('data-tablehead', '');

      Array.from(table.rows).forEach((row) => {
        if (row === headRow) return;
        Array.from(row.cells).forEach((cell, i) => {
          // The leading cell is what the row is *about* — a destination, a recipient — so it
          // becomes the block's heading rather than another labelled field.
          if (i === 0) cell.setAttribute('data-rowhead', '');
          else if (labels[i]) cell.setAttribute('data-label', labels[i]);
        });
      });
    });
  }

  document.querySelectorAll('[data-meningea-legal]').forEach(init);
  document.querySelectorAll('[data-legal-body]').forEach(initLegalDoc);
  document.querySelectorAll('[data-legal-body]').forEach(initLegalTables);
})();
